import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/lib/api-client";
import type { Comparison } from "@/types/paper";

export interface GenerateComparisonInput {
  paper_ids: string[];
  columns: string[];
  custom_columns: string[];
}

export function useComparison(conceptId: string) {
  const queryClient = useQueryClient();
  const key = ["comparison", conceptId] as const;

  const query = useQuery<Comparison | null>({
    queryKey: key,
    queryFn: async () => {
      try {
        return await apiClient
          .get(`api/concepts/${conceptId}/comparison`)
          .json<Comparison>();
      } catch (err: unknown) {
        const status = (err as { response?: { status?: number } })?.response?.status;
        if (status === 404) return null;
        throw err;
      }
    },
    retry: false,
  });

  const generate = useMutation({
    // Keyed so useAnalyzeToolState can observe pending state from another
    // component via useIsMutating (rail RUNNING badge).
    mutationKey: ["comparison-generate", conceptId],
    mutationFn: (input: GenerateComparisonInput) =>
      apiClient
        .post(`api/concepts/${conceptId}/comparison`, {
          json: input,
          timeout: 120_000,
        })
        .json<Comparison>(),
    onSuccess: (data) => {
      queryClient.setQueryData<Comparison>(key, data);
    },
  });

  const clear = useMutation({
    mutationFn: () => apiClient.delete(`api/concepts/${conceptId}/comparison`),
    onSuccess: () => {
      queryClient.setQueryData<Comparison | null>(key, undefined);
    },
  });

  return { ...query, generate, clear };
}
